/**
 * Per-broker monthly AI token cap (Claude + Prime hosted Qwen combined).
 *
 * - < warn threshold (80%): normal.
 * - >= 80%: friendly heads-up in the UI.
 * - >= 100% combined: Claude stops; Prime falls back to the free Modal Qwen
 *   while Qwen is under its own cap, else every AI request is refused until
 *   the month resets (1st of next month, UTC).
 * - Super admins are exempt (never blocked) but their usage is still tracked.
 */

/** Default combined monthly token cap per broker. Override: PRIME_AI_MONTHLY_TOKEN_CAP. */
export const PRIME_AI_MONTHLY_TOKEN_CAP_DEFAULT = 3_000_000;
/** Default Qwen-only cap used for the fallback. Override: PRIME_QWEN_MONTHLY_TOKEN_CAP. */
export const PRIME_QWEN_MONTHLY_TOKEN_CAP_DEFAULT = 3_000_000;
/** Fraction of the cap at which the friendly notice shows. Override: PRIME_AI_TOKEN_CAP_WARN_RATIO. */
export const PRIME_AI_TOKEN_CAP_WARN_RATIO_DEFAULT = 0.8;

export type AiTokenCapState =
  | "ok"
  | "warning"
  /** Combined cap hit: Claude is off, Prime's free Qwen still answers. */
  | "claude_capped"
  /** Combined cap hit and Qwen over its own cap: no AI until reset. */
  | "blocked";

export interface AiTokenCapStatus {
  month: string;
  state: AiTokenCapState;
  exempt: boolean;
  capTokens: number;
  qwenCapTokens: number;
  claudeTokens: number;
  qwenTokens: number;
  usedTokens: number;
  /** 0–100+ (can exceed 100). */
  percentUsed: number;
  /** ISO date (YYYY-MM-DD) when the counter resets. */
  resetsOn: string;
  claudeAllowed: boolean;
  qwenAllowed: boolean;
  /** Friendly message for the UI, null when state is "ok". */
  message: string | null;
}

export function aiTokenCapMonthKey(now: Date = new Date()): string {
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function aiTokenCapResetDate(now: Date = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return d.toISOString().slice(0, 10);
}

export function formatAiCapResetDate(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString("en-CA", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function aiTokenLimitReachedMessage(resetsOn: string): string {
  return `You've reached this month's AI limit; it resets on ${formatAiCapResetDate(resetsOn)}.`;
}

function safe(n: number): number {
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

export function evaluateAiTokenCap(input: {
  claudeTokens: number;
  qwenTokens: number;
  capTokens?: number;
  qwenCapTokens?: number;
  warnRatio?: number;
  exempt?: boolean;
  now?: Date;
}): AiTokenCapStatus {
  const now = input.now ?? new Date();
  const cap = safe(input.capTokens ?? PRIME_AI_MONTHLY_TOKEN_CAP_DEFAULT) ||
    PRIME_AI_MONTHLY_TOKEN_CAP_DEFAULT;
  const qwenCap = safe(input.qwenCapTokens ?? PRIME_QWEN_MONTHLY_TOKEN_CAP_DEFAULT) ||
    PRIME_QWEN_MONTHLY_TOKEN_CAP_DEFAULT;
  const warnRatio =
    input.warnRatio && input.warnRatio > 0 && input.warnRatio < 1
      ? input.warnRatio
      : PRIME_AI_TOKEN_CAP_WARN_RATIO_DEFAULT;
  const claudeTokens = safe(input.claudeTokens);
  const qwenTokens = safe(input.qwenTokens);
  const used = claudeTokens + qwenTokens;
  const percentUsed = Math.round((used / cap) * 1000) / 10;
  const resetsOn = aiTokenCapResetDate(now);
  const exempt = Boolean(input.exempt);

  let state: AiTokenCapState = "ok";
  if (used >= cap) {
    state = qwenTokens < qwenCap ? "claude_capped" : "blocked";
  } else if (used >= cap * warnRatio) {
    state = "warning";
  }
  // Exempt: tracked, still shown the % but never restricted.
  if (exempt) state = "ok";

  const resetLabel = formatAiCapResetDate(resetsOn);
  let message: string | null = null;
  if (state === "warning") {
    message = `Heads up: you've used ${Math.floor(percentUsed)}% of this month's AI. It resets on ${resetLabel}.`;
  } else if (state === "claude_capped") {
    message = `You've used this month's Claude AI. Prime will keep helping with its built-in AI until ${resetLabel}.`;
  } else if (state === "blocked") {
    message = aiTokenLimitReachedMessage(resetsOn);
  }

  return {
    month: aiTokenCapMonthKey(now),
    state,
    exempt,
    capTokens: cap,
    qwenCapTokens: qwenCap,
    claudeTokens,
    qwenTokens,
    usedTokens: used,
    percentUsed,
    resetsOn,
    claudeAllowed: state === "ok" || state === "warning",
    qwenAllowed: state !== "blocked",
    message,
  };
}
